<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useGlobalState } from '@/composables/useGlobalState';
import { useToast } from '@/composables/useToast';
import AccountConfig from './AccountConfig.vue';
import AccountSessions from './AccountSessions.vue';
import AccountShares from './AccountShares.vue';
import AccountApiKeys from './AccountApiKeys.vue';

const { addToast } = useToast();
const { getAuthHeader } = useGlobalState();

const config = ref<{ icsUrl: string; activeProviders: string[]; } | null>(null);
const activeTab = ref<'config' | 'sessions' | 'shares' | 'api-keys'>('config');

const loadConfig = async () => {
    try {
        const response = await fetch('/api/sport/config', { headers: getAuthHeader() });
        config.value = await response.json();
    } catch (e) {
        addToast('Erreur lors du chargement de la configuration', 'error');
    }
};

const switchTab = (tab: 'config' | 'sessions' | 'shares' | 'api-keys') => {
    activeTab.value = tab;
};

onMounted(loadConfig);
</script>

<template>
    <div class="container py-5">
        <!-- Header -->
        <div class="d-flex align-items-center gap-3 mb-5">
            <router-link :to="{ name: 'home_sport' }" class="btn btn-outline-dark rounded-circle p-2 d-flex align-items-center justify-content-center border-0 bg-light" style="width: 2.5rem; height: 2.5rem;">
                <i class="fas fa-arrow-left"></i>
            </router-link>
            <h1 class="mb-0 fw-bold">Mon Compte Sport</h1>
        </div>

        <!-- Tabs Navigation -->
        <ul class="nav nav-pills account-tabs mb-4" role="tablist">
            <li class="nav-item" role="presentation">
                <button
                    class="nav-link d-flex align-items-center gap-2"
                    :class="{ active: activeTab === 'config' }"
                    @click="switchTab('config')"
                >
                    <i class="fas fa-cog"></i> Configuration
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button
                    class="nav-link d-flex align-items-center gap-2"
                    :class="{ active: activeTab === 'sessions' }"
                    @click="switchTab('sessions')"
                >
                    <i class="fas fa-calendar-alt"></i> Mes Sessions
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button
                    class="nav-link d-flex align-items-center gap-2"
                    :class="{ active: activeTab === 'shares' }"
                    @click="switchTab('shares')"
                >
                    <i class="fas fa-users"></i> Partages
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button
                    class="nav-link d-flex align-items-center gap-2"
                    :class="{ active: activeTab === 'api-keys' }"
                    @click="switchTab('api-keys')"
                >
                    <i class="fas fa-key"></i> Clés API
                </button>
            </li>
        </ul>

        <!-- Tab contents -->
        <AccountConfig 
            v-if="activeTab === 'config'" 
            :config="config" 
            @config-updated="loadConfig"
        />

        <AccountSessions 
            v-else-if="activeTab === 'sessions'" 
            :config="config" 
            @switch-tab="switchTab"
        />

        <AccountShares 
            v-else-if="activeTab === 'shares'" 
        />

        <AccountApiKeys 
            v-else-if="activeTab === 'api-keys'" 
        />
    </div>
</template>

<style scoped>
.account-tabs {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 4px;
    gap: 4px;
    display: flex;
}
.account-tabs .nav-link {
    border-radius: 10px;
    color: #6c757d;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 0.6rem 1.25rem;
    border: none;
    transition: all 0.2s ease;
}
.account-tabs .nav-link:hover:not(.active) {
    background: rgba(0,0,0,0.05);
    color: #212529;
}
.account-tabs .nav-link.active {
    background: #212529;
    color: white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
</style>
